import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';

/**
 * Réglages › Connecteurs — `/v1/g/:slug/connecteurs` (M0, `docs/dev/connecteurs.md`).
 * Le serveur décrit les champs ; la régie n'affiche JAMAIS un secret, seulement
 * `finSecret` (« •••• 1a2b »).
 */
export type IdConnecteur = 'brevo' | 'artsy' | 'artnet' | 'stripe' | 'oidc' | 'yousign';
export type TypeChamp = 'texte' | 'email' | 'url' | 'liste-domaines' | 'choix';
export interface ChampConnecteur {
  readonly cle: string;
  readonly requis: boolean;
  readonly type: TypeChamp;
  readonly choix?: readonly string[];
}
export interface ChampsConnecteur {
  readonly secrets: readonly ChampConnecteur[];
  readonly parametres: readonly ChampConnecteur[];
}
export interface VueConnecteur {
  readonly id: IdConnecteur;
  readonly module: string;
  readonly mode: 'simule' | 'reel';
  readonly configure: boolean;
  readonly finSecret: string | null;
  readonly parametres: Readonly<Record<string, string>>;
  readonly verifieLe: string | null;
  readonly erreur: string | null;
  readonly revision: number;
}
export interface ReponseConnecteurs {
  readonly connecteurs: readonly VueConnecteur[];
  readonly champs: Partial<Record<IdConnecteur, ChampsConnecteur>>;
  readonly simulation: boolean;
}
export interface CorpsConnecteur {
  readonly revision: number;
  readonly mode: 'simule' | 'reel';
  readonly secrets?: Readonly<Record<string, string>>;
  readonly parametres: Readonly<Record<string, string>>;
}
export interface Verification {
  readonly ok: boolean;
  readonly erreur: string | null;
  readonly details: Readonly<Record<string, string>>;
}

const chemin = (galerie: string, suite = '') =>
  `/g/${encodeURIComponent(galerie)}/connecteurs${suite}`;

export const connecteursApi = {
  lister: (galerie: string, appel?: AppelApi) =>
    api.get<ReponseConnecteurs>(chemin(galerie), { galerie, ...appel }),
  ecrire: (galerie: string, id: IdConnecteur, corps: CorpsConnecteur, appel?: AppelApi) =>
    api.put<{ connecteur: VueConnecteur }>(chemin(galerie, `/${id}`), corps, { galerie, ...appel }),
  retirer: (galerie: string, id: IdConnecteur, appel?: AppelApi) =>
    api.delete<{ connecteur: VueConnecteur }>(chemin(galerie, `/${id}`), { galerie, ...appel }),
  verifier: (galerie: string, id: IdConnecteur, appel?: AppelApi) =>
    api.post<{ connecteur: VueConnecteur; verification: Verification }>(
      chemin(galerie, `/${id}/verifier`),
      {},
      { galerie, ...appel },
    ),
};

export function useConnecteurs(galerie: string) {
  return useQuery({
    queryKey: cleMetier(galerie, 'connecteurs'),
    queryFn: (contexte) => connecteursApi.lister(galerie, appelDeRequete(contexte)),
  });
}

function invalider(client: ReturnType<typeof useQueryClient>, galerie: string): void {
  void client.invalidateQueries({ queryKey: cleMetier(galerie, 'connecteurs') });
}

export function useEcrireConnecteur(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      gcTime: 0,
      mutationFn: ({ id, corps }: { id: IdConnecteur; corps: CorpsConnecteur }, appel) =>
        connecteursApi.ecrire(galerie, id, corps, appel),
      onSuccess: () => invalider(client, galerie),
    }),
  );
}

export function useRetirerConnecteur(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      gcTime: 0,
      mutationFn: (id: IdConnecteur, appel) => connecteursApi.retirer(galerie, id, appel),
      onSuccess: () => invalider(client, galerie),
    }),
  );
}

export function useVerifierConnecteur(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      gcTime: 0,
      mutationFn: (id: IdConnecteur, appel) => connecteursApi.verifier(galerie, id, appel),
      onSuccess: () => invalider(client, galerie),
    }),
  );
}
